import { config } from "@/config";
import Table from "@commons/Table/TableContent";
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useGetRegisters } from "@services/registers/list";
import { EventRegisterColumns } from "@typings/entities/individuals/additionalData/table";
import { formatDateTime } from "@utils/formatters";
import type { RegisterItemList } from "@services/registers/list/type";
import {
  getEnvironmentLabel,
  getTypeLabel,
} from "@typings/entities/registers/enums";
import type { RegisterItemListWithLabels } from "@utils/translator/registers";
import { useNavigate } from "react-router-dom";

function prepareRegister(
  items: RegisterItemList[],
): RegisterItemListWithLabels[] {
  return items.map((item) => ({
    ...item,
    type: getTypeLabel(item.type) || "-",
    registerDate: formatDateTime(item.registerDate) || "",
    environment: getEnvironmentLabel(item.environment) || "-",
  }));
}

function RegisterTable({ individualId }: { individualId: string }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(config.table.defaultPageSize);
  const navigate = useNavigate();

  const {
    data: registers,
    isLoading,
    error,
  } = useGetRegisters({
    page: page + 1,
    limit: rowsPerPage,
    individualId,
  });

  useEffect(() => {
    if (error) {
      toast.error("Erro ao carregar registros. Tente novamente.");
    }
  }, [error]);

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    [],
  );

  const handleView = (register: RegisterItemListWithLabels) => {
    navigate(`/registers/${register.id}`);
  };

  const heads = EventRegisterColumns;

  const processedData = prepareRegister(registers?.items ?? []);

  if (error) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center py-8">
        <ErrorOutlineIcon className="text-red-500 mb-2 text-6xl" />
        <span className="text-red-600 text-center">
          Ocorreu um erro ao carregar os registros.
        </span>
      </div>
    );
  }

  return (
    <Table.Root isLoading={isLoading}>
      <Table.Head heads={heads} actions={true} />
      <Table.Body
        rows={processedData ?? []}
        columns={heads.map((head) => head.id)}
        actions={["view"]}
        onView={handleView}
      />
      <Table.Footer
        colSpan={heads.length}
        count={registers?.meta.totalItems ?? 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </Table.Root>
  );
}

export default RegisterTable;
